import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Search,
  Add,
  CalendarToday,
  Cancel,
  NotificationAdd,
  EventRepeat,
  FilterList,
  Refresh,
  Visibility,
  SmartToy,
  Close,
} from '@mui/icons-material';
import { api } from '../context/AuthContext';
import ScheduleDirectInterviewModal from '../components/ScheduleDirectInterviewModal';

// ── Status chip styling ──────────────────────────────────────────────────────
const STATUS_COLORS = {
  SCHEDULED:  { bg: '#DBEAFE', color: '#1D4ED8' },
  STARTED:    { bg: '#FEF9C3', color: '#854D0E' },
  COMPLETED:  { bg: '#DCFCE7', color: '#166534' },
  EXPIRED:    { bg: '#F3F4F6', color: '#6B7280' },
  TERMINATED: { bg: '#FEE2E2', color: '#B91C1C' },
  CANCELLED:  { bg: '#FEE2E2', color: '#991B1B' },
};

function StatusChip({ status }) {
  const style = STATUS_COLORS[status] || { bg: '#F3F4F6', color: '#374151' };
  return (
    <Chip
      label={status}
      size="small"
      sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: '0.7rem', height: 22 }}
    />
  );
}

function formatDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata',
  }) + ' IST';
}

// ── Score helpers (same logic as JobInterviewHistory) ────────────────────────
const getScoreColor = (score) => {
  if (score === null || score === undefined) return '#6b7280';
  const s = Number(score);
  if (s >= 80) return '#16a34a';
  if (s >= 60) return '#ea580c';
  return '#dc2626';
};

// ── AI Report Dialog ────────────────────────────────────────────────────────
function AIReportDialog({ open, onClose, interview }) {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !interview?.interview_token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      setReportData(null);
      try {
        const { data } = await api.get(`/direct-interviews/result/${interview.interview_token}`);
        if (!cancelled) setReportData(data.result);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load report.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, interview?.interview_token]);

  const parseEvaluation = () => {
    if (!reportData?.evaluation_details) return null;
    try {
      return typeof reportData.evaluation_details === 'string'
        ? JSON.parse(reportData.evaluation_details)
        : reportData.evaluation_details;
    } catch { return null; }
  };

  const parseAnswers = () => {
    if (!reportData?.answers) return [];
    try {
      return typeof reportData.answers === 'string'
        ? JSON.parse(reportData.answers)
        : reportData.answers || [];
    } catch { return []; }
  };

  const ev = parseEvaluation();
  const answers = parseAnswers();

  const ScoreBar = ({ label, value, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, ml: 2 }}>
        <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e0e0e0', overflow: 'hidden' }}>
          <Box sx={{ width: `${value ?? 0}%`, height: '100%', borderRadius: 3, bgcolor: color, transition: 'width 0.4s ease' }} />
        </Box>
        <Typography variant="caption" fontWeight="bold" sx={{ minWidth: 34, textAlign: 'right', color }}>
          {value ?? '—'}{value !== null && value !== undefined ? '%' : ''}
        </Typography>
      </Box>
    </Box>
  );

  const bandColor = ev?.performanceColor || '#f59e0b';
  const bandLabel = ev?.performanceBand || 'Moderate';

  if (!interview) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToy sx={{ color: '#1E3A8A' }} />
          <Typography variant="h6" fontWeight={700}>AI Interview Report</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Close /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && reportData && (
          <>
            {/* Candidate info */}
            <Box sx={{ mb: 2, p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E5E7EB' }}>
              <Typography variant="body2"><strong>Candidate:</strong> {interview.candidate_first_name} {interview.candidate_last_name}</Typography>
              <Typography variant="body2"><strong>Job Title:</strong> {interview.job_title}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {interview.candidate_email}</Typography>
            </Box>

            {/* Score + Trust chips */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              {reportData.score !== null && reportData.score !== undefined && (
                <Chip
                  label={`Score: ${reportData.score}%`}
                  color={reportData.score >= 70 ? 'success' : reportData.score >= 40 ? 'warning' : 'error'}
                  sx={{ fontWeight: 'bold', fontSize: '1rem' }}
                />
              )}
              {reportData.trust_score !== undefined && reportData.risk_level && (
                <Chip
                  label={`Trust: ${reportData.trust_score}% (${reportData.risk_level.charAt(0).toUpperCase() + reportData.risk_level.slice(1).toLowerCase()} Risk)`}
                  sx={{
                    fontWeight: 'bold', fontSize: '1rem',
                    bgcolor: reportData.risk_level === 'LOW' ? '#d4edda' : reportData.risk_level === 'MEDIUM' ? '#fff3cd' : '#f8d7da',
                    color: reportData.risk_level === 'LOW' ? '#155724' : reportData.risk_level === 'MEDIUM' ? '#856404' : '#721c24',
                  }}
                />
              )}
            </Box>

            {/* Interview Timing */}
            {(reportData.started_at || reportData.submitted_at) && (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0', borderLeft: '5px solid #16a34a' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#16a34a', mb: 1 }}>Interview Timing</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Started:</strong> {reportData.started_at ? new Date(reportData.started_at).toLocaleString() : 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Submitted:</strong> {reportData.submitted_at ? new Date(reportData.submitted_at).toLocaleString() : 'N/A'}
                </Typography>
                {reportData.started_at && reportData.submitted_at && (() => {
                  const dur = Math.floor((new Date(reportData.submitted_at) - new Date(reportData.started_at)) / 1000);
                  return <Typography variant="body2"><strong>Duration:</strong> {Math.floor(dur / 60)} min {dur % 60} sec</Typography>;
                })()}
              </Box>
            )}

            {/* Performance Score Breakdown */}
            {ev && (
              <>
                <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: `2px solid ${bandColor}`, bgcolor: `${bandColor}10`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                      Overall AI Performance
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography variant="h3" fontWeight="bold" sx={{ color: bandColor, lineHeight: 1 }}>
                        {ev.finalScore ?? reportData.score ?? 0}
                      </Typography>
                      <Typography variant="h6" color="text.secondary">/100</Typography>
                    </Box>
                  </Box>
                  <Chip label={bandLabel} size="medium" sx={{ fontWeight: 700, fontSize: '0.85rem', bgcolor: bandColor, color: '#fff', px: 1 }} />
                </Box>

                {ev.componentScores && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: '#fafafa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#444' }}>Score Breakdown</Typography>
                    {ev.componentScores.qa != null && <ScoreBar label="Q&A / Communication" value={ev.componentScores.qa} color="#1976d2" />}
                    {ev.componentScores.coding != null && <ScoreBar label="Coding Assessment" value={ev.componentScores.coding} color="#7b1fa2" />}
                    {ev.componentScores.sql != null && <ScoreBar label="SQL Assessment" value={ev.componentScores.sql} color="#00897b" />}
                    {ev.componentScores.architecture != null && <ScoreBar label="Architecture Design" value={ev.componentScores.architecture} color="#2e7d32" />}
                    <ScoreBar label="Participation" value={ev.componentScores.participation} color="#e65100" />
                    <ScoreBar label="Integrity / Proctoring" value={ev.componentScores.proctoring}
                      color={!reportData.risk_level || reportData.risk_level === 'LOW' ? '#2e7d32' : reportData.risk_level === 'MEDIUM' ? '#f57c00' : '#c62828'} />
                  </Box>
                )}

                {ev.participationEvaluation && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: '#fff8e1', borderRadius: 2, border: '1px solid #ffe082', borderLeft: '5px solid #f9a825' }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#e65100', mb: 1 }}>Question Completion</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {[
                        { label: 'Questions Answered', value: `${ev.participationEvaluation.questionsAnswered} / ${ev.participationEvaluation.totalQuestions}` },
                        { label: 'Completion Rate', value: `${ev.participationEvaluation.completionRate}%` },
                        { label: 'Unanswered', value: ev.participationEvaluation.unansweredQuestions },
                        { label: 'Terminated Midway', value: ev.participationEvaluation.terminatedMidInterview ? 'Yes' : 'No' },
                        { label: 'Participation Score', value: `${ev.participationEvaluation.score}/100` },
                      ].map((item) => (
                        <Box key={item.label} sx={{ minWidth: 130 }}>
                          <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                          <Typography variant="body2" fontWeight="bold">{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Q&A Evaluation */}
                {ev.qaEvaluation && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 2, border: '1px solid #90caf9', borderLeft: '5px solid #1976d2' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1976d2' }}>Q&A Evaluation</Typography>
                      <Chip label={`Score: ${ev.qaEvaluation.score}/100`} size="small"
                        sx={{ fontWeight: 700, bgcolor: '#1976d2', color: '#fff', fontSize: '0.75rem' }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {[
                        { label: 'Accuracy', value: `${ev.qaEvaluation.accuracyScore}%` },
                        { label: 'Communication', value: `${ev.qaEvaluation.communicationScore}%` },
                        { label: 'Technical Depth', value: `${ev.qaEvaluation.technicalDepthScore}%` },
                        { label: 'Completeness', value: `${ev.qaEvaluation.completenessScore}%` },
                        { label: 'Answered', value: `${ev.qaEvaluation.answeredCount} / ${ev.qaEvaluation.questionsCount}` },
                      ].map((item) => (
                        <Box key={item.label} sx={{ minWidth: 130 }}>
                          <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                          <Typography variant="body2" fontWeight="bold">{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Coding Evaluation */}
                {ev.codingEvaluation && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: '#f3e5f5', borderRadius: 2, border: '1px solid #ce93d8', borderLeft: '5px solid #7b1fa2' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#7b1fa2' }}>Coding Assessment</Typography>
                      <Chip label={`Score: ${ev.codingEvaluation.score}/100`} size="small"
                        sx={{ fontWeight: 700, bgcolor: '#7b1fa2', color: '#fff', fontSize: '0.75rem' }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      {[
                        { label: 'Questions', value: ev.codingEvaluation.questionsCount },
                        { label: 'Test Cases Passed', value: `${ev.codingEvaluation.testCasesPassed}%` },
                        { label: 'Compilation', value: `${ev.codingEvaluation.compilationRate}%` },
                      ].map((item) => (
                        <Box key={item.label} sx={{ minWidth: 130 }}>
                          <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                          <Typography variant="body2" fontWeight="bold">{item.value}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* SQL Evaluation */}
                {ev.sqlEvaluation && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: '#e0f2f1', borderRadius: 2, border: '1px solid #80cbc4', borderLeft: '5px solid #00897b' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#00695c' }}>SQL Assessment</Typography>
                      <Chip label={`Score: ${ev.sqlEvaluation.score}/100`} size="small"
                        sx={{ fontWeight: 700, bgcolor: '#00897b', color: '#fff', fontSize: '0.75rem' }} />
                    </Box>
                  </Box>
                )}

                {/* Architecture Evaluation */}
                {ev.architectureEvaluation && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #a5d6a7', borderLeft: '5px solid #2e7d32' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#2e7d32' }}>Architecture Assessment</Typography>
                      <Chip label={`Score: ${ev.architectureEvaluation.score}/100`} size="small"
                        sx={{ fontWeight: 700, bgcolor: '#2e7d32', color: '#fff', fontSize: '0.75rem' }} />
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/* Performance Assessment */}
            {reportData.feedback && (
              <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: '1px solid #e0e0e0', borderLeft: '6px solid #1E3A8A', bgcolor: '#f8fafc' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1E3A8A', mb: 0.5 }}>Performance Assessment</Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6, fontWeight: 500 }}>
                  {reportData.feedback}
                </Typography>
              </Box>
            )}

            {/* Behavioral Analysis */}
            {reportData.behavior_summary && (
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f4f8ff', borderRadius: 2, border: '1px solid #d1e3ff', borderLeft: '6px solid #1976d2' }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1976d2', mb: 1 }}>Behavioral Analysis</Typography>
                {(() => {
                  try {
                    const summary = typeof reportData.behavior_summary === 'string'
                      ? JSON.parse(reportData.behavior_summary)
                      : reportData.behavior_summary;
                    return (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                          <strong>Confidence:</strong> {summary.confidence || 'N/A'}
                        </Typography>
                        {(summary.observations || []).map((obs, i) => (
                          <Typography key={i} variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>• {obs}</Typography>
                        ))}
                      </>
                    );
                  } catch { return null; }
                })()}
              </Box>
            )}

            {/* Trust Assessment */}
            {reportData.trust_score !== undefined && reportData.risk_level && (
              <Box sx={{ mb: 2, p: 2, borderRadius: 2,
                bgcolor: reportData.risk_level === 'LOW' ? '#d4edda20' : reportData.risk_level === 'MEDIUM' ? '#fff3cd20' : '#f8d7da20',
                border: `1px solid ${reportData.risk_level === 'LOW' ? '#d4edda' : reportData.risk_level === 'MEDIUM' ? '#fff3cd' : '#f8d7da'}`,
                borderLeft: `6px solid ${reportData.risk_level === 'LOW' ? '#28a745' : reportData.risk_level === 'MEDIUM' ? '#ffc107' : '#dc3545'}`
              }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{
                  color: reportData.risk_level === 'LOW' ? '#155724' : reportData.risk_level === 'MEDIUM' ? '#856404' : '#721c24',
                  mb: 1
                }}>Trust Assessment</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  <strong>{(reportData.risk_level || 'UNKNOWN').toUpperCase()} RISK</strong>
                  {(() => {
                    try {
                      const v = typeof reportData.violations === 'string'
                        ? JSON.parse(reportData.violations) : reportData.violations || {};
                      const flags = Object.entries(v).filter(([, c]) => c > 0).map(([k]) => k.replace(/([A-Z])/g, ' $1').trim());
                      return flags.length > 0 ? ` — ${flags.join(', ')}` : '';
                    } catch { return ''; }
                  })()}
                </Typography>
              </Box>
            )}

            {/* Suspicious Activity Timeline */}
            {reportData.violation_events && (() => {
              const events = Array.isArray(reportData.violation_events)
                ? reportData.violation_events
                : (() => { try { return JSON.parse(reportData.violation_events); } catch { return []; } })();
              if (events.length === 0) return null;
              return (
                <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', bgcolor: '#fafafa', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Suspicious Activity Timeline</Typography>
                  {events.slice(0, 10).map((event, i) => {
                    const mins = Math.floor((event.time || 0) / 60);
                    const secs = (event.time || 0) % 60;
                    return (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, p: 1.2, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #f0f0f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', minWidth: 50 }}>
                            [{String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}]
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 2, fontWeight: 500 }}>
                            {(event.type || '').replace(/([A-Z])/g, ' $1').trim()}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}

            {/* Q&A Transcript */}
            {answers.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#333' }}>
                  Interview Transcript ({answers.length} questions)
                </Typography>
                {answers.map((a, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ p: 1.5, bgcolor: '#EEF2FF' }}>
                      <Typography variant="body2" fontWeight={600} color="#1E40AF">
                        Q{idx + 1}: {a.question || a.text || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1.5 }}>
                      {a.questionType === 'coding' ? (
                        <Box>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
                            Language: {a.selectedLanguage || 'Unknown'}
                          </Typography>
                          <Box sx={{ p: 1.5, bgcolor: '#1e1e1e', color: '#d4d4d4', borderRadius: 1, overflowX: 'auto', border: '1px solid #333' }}>
                            <pre style={{ margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                              <code>{a.sourceCode || a.answer || a.response || 'No code submitted'}</code>
                            </pre>
                          </Box>
                          {(a.terminalOutput || a.executionOutput || a.compileError || a.runtimeError) && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" fontWeight="bold" sx={{ color: '#555', mb: 0.5, display: 'block' }}>Terminal Output:</Typography>
                              <Box sx={{ p: 1, bgcolor: '#000', color: '#00ff00', borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', maxHeight: 150, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                                {a.terminalOutput || a.executionOutput || a.compileError || a.runtimeError || 'No output'}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {a.answer || a.response || 'No response recorded'}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </>
        )}

        {!loading && !reportData && !error && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">No report data available for this interview.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Reschedule dialog ────────────────────────────────────────────────────────
function RescheduleDialog({ open, onClose, interview, onRescheduled }) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = async () => {
    if (!date || !time) { setError('Date and time are required.'); return; }
    setError('');
    setLoading(true);
    try {
      const scheduledAt = new Date(`${date}T${time}:00`).toISOString();
      const { data } = await api.patch(`/direct-interviews/${interview.id}/reschedule`, { scheduledAt });
      onRescheduled(data.interview);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to reschedule.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle fontWeight={700}>Reschedule Interview</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Candidate: <strong>{interview?.candidate_first_name} {interview?.candidate_last_name}</strong>
          </Typography>
          <TextField
            label="New Date *" type="date" value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }} inputProps={{ min: today }}
            size="small" fullWidth disabled={loading}
          />
          <TextField
            label="New Time *" type="time" value={time}
            onChange={(e) => setTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small" fullWidth disabled={loading}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}>
          Reschedule
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function DirectInterviews() {
  const [interviews, setInterviews]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  // Filters
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');

  // Modals
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [cancelTarget, setCancelTarget]         = useState(null);
  const [cancelling, setCancelling]             = useState(false);
  const [reportTarget, setReportTarget]         = useState(null);

  // Toast
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') =>
    setToast({ open: true, message, severity });

  // ── Fetch ──────────────────────────────────────────────────────────────
  const fetchInterviews = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (status)   params.set('status', status);
      if (search)   params.set('search', search);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo)   params.set('dateTo', dateTo);
      const { data } = await api.get(`/direct-interviews?${params.toString()}`);
      setInterviews(data.interviews || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load direct interviews.');
    } finally {
      setLoading(false);
    }
  }, [status, search, dateFrom, dateTo]);

  useEffect(() => {
    fetchInterviews();
  }, [fetchInterviews]);

  // ── Cancel ─────────────────────────────────────────────────────────────
  const handleCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await api.patch(`/direct-interviews/${cancelTarget.id}/cancel`);
      setInterviews((prev) =>
        prev.map((i) => i.id === cancelTarget.id ? { ...i, status: 'CANCELLED' } : i)
      );
      showToast('Interview cancelled successfully.');
    } catch (e) {
      showToast(e?.response?.data?.message || 'Failed to cancel interview.', 'error');
    } finally {
      setCancelling(false);
      setCancelTarget(null);
    }
  };

  // ── Send Reminder ──────────────────────────────────────────────────────
  const handleReminder = async (interview) => {
    try {
      await api.post(`/direct-interviews/${interview.id}/remind`);
      showToast(`Reminder sent to ${interview.candidate_email}.`);
    } catch (e) {
      showToast(e?.response?.data?.message || 'Failed to send reminder.', 'error');
    }
  };

  // ── Schedule success ───────────────────────────────────────────────────
  const handleScheduleSuccess = (result) => {
    setScheduleOpen(false);
    showToast(
      `${result.scheduledCount} interview(s) scheduled successfully.` +
      (result.failedCount > 0 ? ` ${result.failedCount} failed.` : '')
    );
    fetchInterviews();
  };

  // ── Reschedule success ─────────────────────────────────────────────────
  const handleRescheduled = (updated) => {
    setInterviews((prev) => prev.map((i) => i.id === updated.id ? { ...i, ...updated } : i));
    showToast('Interview rescheduled and candidate notified.');
    setRescheduleTarget(null);
  };

  const clearFilters = () => {
    setSearch(''); setStatus(''); setDateFrom(''); setDateTo('');
  };

  const hasFilters = search || status || dateFrom || dateTo;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
                   Direct Interviews
                  </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage AI interviews without requiring a job application.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setScheduleOpen(true)}
          sx={{ borderRadius: 2, fontWeight: 700, px: 3, py: 1.25 }}
        >
          Schedule Direct Interview
        </Button>
      </Box>

      {/* ── Filter Bar ─────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2, mb: 2.5 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search candidate, email, job title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="">All Statuses</MenuItem>
              {['SCHEDULED', 'STARTED', 'COMPLETED', 'TERMINATED', 'EXPIRED', 'CANCELLED'].map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="From Date" type="date" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 170 }}
          />

          <TextField
            label="To Date" type="date" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }} size="small"
            sx={{ minWidth: 170 }}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            {hasFilters && (
              <Tooltip title="Clear filters">
                <Button variant="text" size="small" onClick={clearFilters} color="inherit" startIcon={<FilterList />}>
                  Clear
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={fetchInterviews}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* ── Error ──────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* ── Table ──────────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflowX: 'auto' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#F8FAFC', fontWeight: 700, fontSize: '0.8rem', color: '#374151', borderBottom: '2px solid #E5E7EB' } }}>
                <TableCell>Candidate</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Job Title</TableCell>
                <TableCell>Interview Date</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Score</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : interviews.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Box sx={{ color: 'text.secondary' }}>
                      <Typography variant="body1" fontWeight={600}>No direct interviews found</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {hasFilters ? 'Try adjusting your filters.' : 'Click "+ Schedule Direct Interview" to get started.'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                interviews.map((interview) => (
                  <TableRow
                    key={interview.id}
                    hover
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {interview.candidate_first_name} {interview.candidate_last_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {interview.candidate_email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {interview.job_title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(interview.scheduled_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {interview.interview_duration_minutes ? `${interview.interview_duration_minutes} mins` : '60 mins'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={interview.status} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Direct Interview"
                        size="small"
                        sx={{ bgcolor: '#F0FDF4', color: '#166534', fontWeight: 600, fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      {['COMPLETED', 'TERMINATED'].includes(interview.status) && interview.ai_score !== null && interview.ai_score !== undefined ? (
                        <Typography variant="body2" sx={{ fontWeight: 700, color: getScoreColor(interview.ai_score) }}>
                          {interview.ai_score}%
                        </Typography>
                      ) : ['COMPLETED', 'TERMINATED'].includes(interview.status) ? (
                        <Typography variant="caption" color="text.secondary">Processing…</Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                        {/* View AI Report */}
                        {['COMPLETED', 'TERMINATED'].includes(interview.status) && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Visibility sx={{ fontSize: 16 }} />}
                            onClick={() => setReportTarget(interview)}
                            sx={{
                              borderRadius: '8px', textTransform: 'none', fontWeight: 'bold',
                              color: '#1E3A8A', borderColor: '#1E3A8A', fontSize: '0.75rem',
                              '&:hover': { bgcolor: 'rgba(30, 58, 138, 0.04)', borderColor: '#1E40AF' },
                            }}
                          >
                            View AI Report
                          </Button>
                        )}

                        {/* Send Reminder */}
                        {['SCHEDULED', 'STARTED'].includes(interview.status) && (
                          <Tooltip title="Send Reminder">
                            <IconButton
                              size="small"
                              onClick={() => handleReminder(interview)}
                              sx={{ color: '#1E3A8A' }}
                            >
                              <NotificationAdd fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Reschedule */}
                        {['SCHEDULED', 'COMPLETED', 'TERMINATED', 'EXPIRED'].includes(interview.status) && (
                          <Tooltip title="Reschedule">
                            <IconButton
                              size="small"
                              onClick={() => setRescheduleTarget(interview)}
                              sx={{ color: '#0369A1' }}
                            >
                              <EventRepeat fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Cancel */}
                        {['SCHEDULED', 'STARTED'].includes(interview.status) && (
                          <Tooltip title="Cancel Interview">
                            <IconButton
                              size="small"
                              onClick={() => setCancelTarget(interview)}
                              sx={{ color: 'error.main' }}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Row count */}
        {!loading && interviews.length > 0 && (
          <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #E5E7EB', bgcolor: '#F8FAFC' }}>
            <Typography variant="caption" color="text.secondary">
              {interviews.length} interview{interviews.length !== 1 ? 's' : ''} found
            </Typography>
          </Box>
        )}
      </Paper>

      {/* ── Schedule Modal ──────────────────────────────────────────────── */}
      <ScheduleDirectInterviewModal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        onSuccess={handleScheduleSuccess}
      />

      {/* ── Reschedule Dialog ───────────────────────────────────────────── */}
      {rescheduleTarget && (
        <RescheduleDialog
          open={!!rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          interview={rescheduleTarget}
          onRescheduled={handleRescheduled}
        />
      )}

      {/* ── Cancel Confirmation ─────────────────────────────────────────── */}
      <Dialog
        open={!!cancelTarget}
        onClose={() => !cancelling && setCancelTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>Cancel Interview?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel the interview for{' '}
            <strong>{cancelTarget?.candidate_first_name} {cancelTarget?.candidate_last_name}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelTarget(null)} disabled={cancelling} color="inherit">
            Keep Interview
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {cancelling ? 'Cancelling…' : 'Cancel Interview'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── AI Report Dialog ──────────────────────────────────────────── */}
      <AIReportDialog
        open={!!reportTarget}
        onClose={() => setReportTarget(null)}
        interview={reportTarget}
      />

      {/* ── Toast ──────────────────────────────────────────────────────── */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ borderRadius: 2, minWidth: 320 }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
